import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_HEADING = "Youth sports, from both sides of the whistle.";
const DEFAULT_SUB =
  "One worthwhile read each week on coaches, players, parents, culture, and everything happening between them.";

/**
 * Reusable VoluntoldHQ newsletter signup.
 * Official palette: Charcoal #222222 · Amber #DB9130 · Cream #F5F0EA
 * Email only. Never assigns persona, never triggers nurture (backend is neutral).
 */
export default function NewsletterSignup({
  source = "footer",
  tone = "light", // "light" (cream bg) | "dark" (charcoal bg)
  showCopy = true,
  showRole = false,
  heading = DEFAULT_HEADING,
  sub = DEFAULT_SUB,
  onSuccess,
  testId = "newsletter-signup",
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("both");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const dark = tone === "dark";
  const inputCls = dark
    ? "bg-[#33434E] border-[#3E4E59] text-[#F5F0EA] placeholder:text-[#8A97A0] focus:border-[#DB9130]"
    : "bg-white border-[#CFC7BA] text-[#222222] placeholder:text-[#9AA1A6] focus:border-[#222222]";
  const headCls = dark ? "text-[#F5F0EA]" : "text-[#222222]";
  const subCls = dark ? "text-[#A9B6BF]" : "text-[#5B6770]";

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    if (!EMAIL_RE.test(email.trim())) {
      setStatus("error");
      setMessage("That email doesn't look right — mind checking it?");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      await api.post("/newsletter", { email: email.trim(), source, ...(showRole ? { role } : {}) });
      trackEvent("newsletter_signup", { source });
      setStatus("success");
      try { localStorage.setItem("vt_news_popup", "subscribed"); } catch { /* ignore */ }
      setMessage(showRole ? `YOU'RE IN · ${role.toUpperCase()}` : "You're in. One worthwhile read a week — nothing else.");
      setEmail("");
      onSuccess && onSuccess();
    } catch (err) {
      setStatus("error");
      const code = err?.response?.status;
      setMessage(
        code === 422
          ? "That email doesn't look right — mind checking it?"
          : "Something went wrong on our end. Try again in a moment."
      );
    }
  };

  if (status === "success") {
    return (
      <div data-testid={`${testId}-success`} className={`flex items-start gap-3 ${subCls}`}>
        <span
          className="mt-0.5 h-6 w-6 shrink-0 flex items-center justify-center rounded-full"
          style={{ background: "#DB9130", color: "#222222" }}
        >
          <Check size={15} strokeWidth={3} />
        </span>
        <p className={`text-sm leading-relaxed ${dark ? "text-[#F5F0EA]" : "text-[#222222]"}`}>{message}</p>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      {showCopy && (
        <>
          <h3 className={`font-head font-semibold uppercase tracking-wide text-lg ${headCls}`}>{heading}</h3>
          <p className={`mt-2 text-sm leading-relaxed ${subCls}`}>{sub}</p>
        </>
      )}
      {showRole && (
        <div className="flex gap-2 mb-3" data-testid={`${testId}-roles`}>
          {["coach", "parent", "both"].map((r) => {
            const on = role === r;
            return (
              <button key={r} type="button" onClick={() => setRole(r)}
                data-testid={`${testId}-role-${r}`}
                aria-pressed={on}
                className={`flex-1 font-head uppercase tracking-wide text-xs py-2.5 border-2 transition-colors ${on ? "bg-[color:var(--vt-dark)] text-[color:var(--vt-cream)] border-[color:var(--vt-dark)]" : "bg-transparent text-[color:var(--vt-dark)] border-[color:var(--vt-dark)] hover:bg-[color:var(--vt-dark)]/5"}`}>
                {r}
              </button>
            );
          })}
        </div>
      )}
      <form onSubmit={submit} className={`${showCopy ? "mt-4" : ""} flex flex-col sm:flex-row gap-2`} noValidate>
        <label htmlFor={`${testId}-input`} className="sr-only">Email address</label>
        <input
          id={`${testId}-input`}
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
          placeholder="you@email.com"
          disabled={status === "loading"}
          aria-invalid={status === "error"}
          data-testid={`${testId}-input`}
          className={`flex-1 min-w-0 border-2 px-4 py-3 text-sm focus:outline-none transition-colors disabled:opacity-60 ${inputCls}`}
        />
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid={`${testId}-submit`}
          className="inline-flex items-center justify-center gap-2 bg-[#DB9130] text-[#222222] font-head uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#C67F22] transition-colors disabled:opacity-60"
        >
          {status === "loading" ? <><Loader2 size={15} className="animate-spin" /> Joining…</> : "Join Voluntold"}
        </button>
      </form>
      {status === "error" && (
        <p data-testid={`${testId}-error`} className="mt-2 text-sm text-[#C0492B]">{message}</p>
      )}
    </div>
  );
}
